/* eslint-disable react/no-danger */
import { Modal, Tooltip } from '@pmi/dsm-react';
import { AlertQuestionIcon } from '@pmi/dsm-react/dist/components/icons/fa/16';
import { PlainIcon } from '@pmi/dsm-react/dist/components/icons/tt/mark/fc';
import {
  LeadershipIcon,
  StrategicIcon,
  TechnicalIcon,
} from '@pmi/dsm-react/dist/components/icons/tt/breakdown';
import styles from './productDetailsModal.module.scss';
import { ProductApiModel } from '../../../../../shared/models/ProductModel';
import CategoryLabel from '../../../../../shared/components/CategoryLabel';
import { ProductTypes } from '../../../../../shared/constants';

interface DetailModalProps {
  product: ProductApiModel;
  onCancel: () => void;
}

export default function ProductDetailModal({
  product,
  onCancel,
}: DetailModalProps) {
  const {
    name,
    fullDescription = '',
    type,
    level,
    waysOfWorkingPDUs,
    businessAcumenPDUs,
    powerSkillsPDUs,
    totalPDUs,
  } = product;

  return (
    <Modal
      show
      customClassName={`d-flex flex-column ${styles.productCard}`}
      variant="input"
      onClose={onCancel}
      buttons={[
        {
          titleText: 'Cancel',
          variant: 'outline-secondary',
          onClick: onCancel,
        },
      ]}
    >
      <h4
        className="d-inline-block mb-0"
        data-testid="product-name"
        dangerouslySetInnerHTML={{ __html: name }}
      />
      <div>
        {!product?.type
          .toLowerCase()
          .includes(ProductTypes.membership.toLowerCase()) &&
          product?.bundle && (
            <CategoryLabel type="Bundle" className="mb-3 me-2 float-start" />
          )}
        <CategoryLabel type={type} className="mb-3 d-inline-flex" />
      </div>
      <div className="label mb-4" data-testid="msg2">
        {type === 'eLearning' && (
          <>
            {level && <span data-testid="course-level">Level: {level}</span>}
            {level && totalPDUs && ' | '}
            {totalPDUs && `PDUs: ${totalPDUs}`}
          </>
        )}
        {type === 'Certification' && (
          <span>Application, Exam and Certification included</span>
        )}
        {type === ProductTypes.membership && (
          <span>Annual Membership good for 1 year</span>
        )}
      </div>
      {type === 'eLearning' && (
        <>
          <div className="mb-2 mb-sm-4 d-flex align-items-center">
            <b className="pe-2">Talent Triangle PDUs</b>
            <Tooltip
              className={`${styles.tooltip} darkTooltip`}
              type="sm"
              placement="top"
              content={
                <>
                  <b>Talent Triangle PDUs</b>
                  <div>
                    As a certification holder you must earn a minimum number of
                    Professional Development Units (PDUs) in each of the skill
                    areas of the PMI Talent Triangle - Ways of Working (formerly
                    Technical), Power Skills (formerly Leadership), Business
                    Acumen (formerly Strategic).{' '}
                    <a
                      href="https://www.pmi.org/certifications/certification-resources/maintain/earn-pdus"
                      target="_blank"
                      rel="noreferrer"
                      className="body5-link-violet"
                    >
                      Learn more about PDUs
                    </a>
                    .
                  </div>
                </>
              }
            >
              <div className="d-flex">
                <AlertQuestionIcon fontSize="xs" />
                <span className="ps-1 text-link--xs">What is this?</span>
              </div>
            </Tooltip>
          </div>
          <div className="mb-2 mb-md-4 d-flex gap-4 flex-row justify-content-around">
            <div className="d-flex flex-column align-items-center text-center">
              <TechnicalIcon
                color="original"
                fontSize="lg"
                className={`ps-0 pe-0 ${styles.technicalIcon}`}
              />
              <div className="text-link--sm pt-1 pb-2">Ways of Working</div>
              <div
                className="text-link--lg fw-bold"
                data-testid="ways-of-working-pdus"
              >
                {waysOfWorkingPDUs ?? 0}
              </div>
            </div>
            <div className="d-flex flex-column align-items-center text-center">
              <StrategicIcon
                color="original"
                fontSize="lg"
                className={`ps-0 pe-0 ${styles.strategicIcon}`}
              />
              <div className="text-link--sm pt-1 pb-2">Business Acumen</div>
              <div
                className="text-link--lg fw-bold"
                data-testid="business-acumen-pdus"
              >
                {businessAcumenPDUs ?? 0}
              </div>
            </div>
            <div className="d-flex flex-column align-items-center text-center">
              <LeadershipIcon
                color="original"
                fontSize="lg"
                className={`ps-0 pe-0 ${styles.leadershipIcon}`}
              />
              <div className="text-link--sm pt-1 pb-2">Power Skills</div>
              <div
                className="text-link--lg fw-bold"
                data-testid="power-scills-pdus"
              >
                {powerSkillsPDUs ?? 0}
              </div>
            </div>
            <div className="d-flex flex-column align-items-center text-center content-label">
              <div className={`${styles.scaledIcon}`}>
                <PlainIcon
                  color="original"
                  fontSize="lg"
                  className="ps-0 pe-0"
                />
              </div>
              <div className="text-link--sm pt-1 pb-2">Total</div>
              <div className="text-link--lg fw-bold">{totalPDUs ?? 0}</div>
            </div>
          </div>
        </>
      )}
      <div
        className="mb-4"
        data-testid="description"
        dangerouslySetInnerHTML={{ __html: fullDescription }}
      />
    </Modal>
  );
}
